import { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCourse } from '@/hooks/useCourses';
import { useLessonProgress, useToggleLessonCompletion } from '@/hooks/useLessonProgress';
import { Button } from '@/components/ui/button';
import { ChevronLeft, Lock } from 'lucide-react';
import {
  getCourseProgress,
  getPublishedLessonsFlat,
  getAdjacentLessons,
  findModuleForLesson,
} from '@/types/courses';
import type { CourseLesson } from '@/types/courses';
import { PlayerVideoSection } from './player/PlayerVideoSection';
import { PlayerControls } from './player/PlayerControls';
import { PlayerLessonContent } from './player/PlayerLessonContent';
import { PlayerSidebarTOC } from './player/PlayerSidebarTOC';
import { PlayerMobileNav } from './player/PlayerMobileNav';
import { PlayerMobileTOC } from './player/PlayerMobileTOC';

interface CoursePlayerProps {
  basePath: string;
  courseType: 'standard' | 'nextgen';
}

export function CoursePlayer({ basePath, courseType }: CoursePlayerProps) {
  const { courseId, lessonId } = useParams<{
    courseId: string;
    moduleId?: string;
    lessonId?: string;
  }>();
  const navigate = useNavigate();
  const { data: course, isLoading } = useCourse(courseId);
  const { data: completedLessons = {} } = useLessonProgress();
  const toggleCompletion = useToggleLessonCompletion();

  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());
  const [showMobileTOC, setShowMobileTOC] = useState(false);

  // Published lessons flat list
  const publishedLessons = useMemo(
    () => (course ? getPublishedLessonsFlat(course) : []),
    [course]
  );

  // Currently selected lesson
  const selectedLesson = useMemo(() => {
    if (!lessonId || !course) return null;
    return course.modules
      .flatMap((m) => m.lessons)
      .find((l) => l.id === lessonId) || null;
  }, [course, lessonId]);

  // Auto-navigate to first published lesson when course loads without a lesson selected
  useEffect(() => {
    if (!course || lessonId) return;
    if (publishedLessons.length > 0) {
      const first = publishedLessons[0];
      const mod = findModuleForLesson(course, first.id);
      if (mod) {
        navigate(`${basePath}/${course.id}/${mod.id}/${first.id}`, { replace: true });
      }
    }
  }, [course, lessonId, publishedLessons, basePath, navigate]);

  // Auto-expand module containing selected lesson
  useEffect(() => {
    if (!course || !lessonId) {
      // Expand first module by default
      if (course?.modules.length) {
        setExpandedModules(new Set([course.modules[0].id]));
      }
      return;
    }
    const mod = findModuleForLesson(course, lessonId);
    if (mod) {
      setExpandedModules((prev) => {
        const next = new Set(prev);
        next.add(mod.id);
        return next;
      });
    }
  }, [course, lessonId]);

  // Progress
  const progress = useMemo(
    () => (course ? getCourseProgress(course, completedLessons) : { total: 0, done: 0, percent: 0 }),
    [course, completedLessons]
  );

  // Adjacent lessons
  const { previous: previousLesson, next: nextLesson } = useMemo(
    () =>
      course && selectedLesson
        ? getAdjacentLessons(course, selectedLesson.id)
        : { previous: null, next: null },
    [course, selectedLesson]
  );

  const toggleModule = (modId: string) => {
    setExpandedModules((prev) => {
      const next = new Set(prev);
      if (next.has(modId)) next.delete(modId);
      else next.add(modId);
      return next;
    });
  };

  const navigateToLesson = (lesson: CourseLesson) => {
    if (!course) return;
    const mod = findModuleForLesson(course, lesson.id);
    if (mod) {
      navigate(`${basePath}/${course.id}/${mod.id}/${lesson.id}`);
    }
  };

  const handleToggleComplete = () => {
    if (!selectedLesson || !courseId) return;
    const mod = findModuleForLesson(course!, selectedLesson.id);
    toggleCompletion.mutate({
      lessonId: selectedLesson.id,
      courseId,
      courseType,
      isCompleted: !!completedLessons[selectedLesson.id],
      lessonTitle: selectedLesson.title,
      moduleId: mod?.id,
    });
  };

  const handleCompleteAndNext = () => {
    if (selectedLesson && courseId && !completedLessons[selectedLesson.id]) {
      const mod = findModuleForLesson(course!, selectedLesson.id);
      toggleCompletion.mutate({
        lessonId: selectedLesson.id,
        courseId,
        courseType,
        isCompleted: false,
        lessonTitle: selectedLesson.title,
        moduleId: mod?.id,
      });
    }
    if (nextLesson) navigateToLesson(nextLesson);
  };

  const handleToggleLessonComplete = (e: React.MouseEvent, lid: string) => {
    e.stopPropagation();
    if (!courseId || !course) return;
    const lesson = publishedLessons.find((l) => l.id === lid);
    const mod = findModuleForLesson(course, lid);
    toggleCompletion.mutate({
      lessonId: lid,
      courseId,
      courseType,
      isCompleted: !!completedLessons[lid],
      lessonTitle: lesson?.title,
      moduleId: mod?.id,
    });
  };

  const handleVideoEnded = () => {
    if (selectedLesson && courseId && !completedLessons[selectedLesson.id]) {
      const mod = findModuleForLesson(course!, selectedLesson.id);
      toggleCompletion.mutate({
        lessonId: selectedLesson.id,
        courseId,
        courseType,
        isCompleted: false,
        lessonTitle: selectedLesson.title,
        moduleId: mod?.id,
      });
    }
  };

  // Loading
  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-6 animate-pulse">
        <div className="h-7 w-64 bg-gray-200 dark:bg-gray-700 rounded mb-4" />
        <div className="aspect-video bg-gray-200 dark:bg-gray-700 rounded-xl mb-4" />
        <div className="h-10 w-full bg-gray-200 dark:bg-gray-700 rounded" />
      </div>
    );
  }

  // Course not found
  if (!course) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-gray-500">
        <p className="mb-4">Course not found.</p>
        <Button variant="outline" onClick={() => navigate(basePath)}>
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back to Courses
        </Button>
      </div>
    );
  }

  // All lessons Coming Soon — empty state
  if (publishedLessons.length === 0) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-6 pb-20">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">
          {course.title}
        </h2>
        <div className="bg-black aspect-video flex items-center justify-center rounded-xl mb-6">
          <div className="text-center text-gray-400">
            <Lock className="h-16 w-16 mx-auto mb-2" />
            <p className="text-sm">No lessons available yet</p>
          </div>
        </div>
        <div className="text-center py-12 border border-dashed border-gray-200 dark:border-gray-700 rounded-xl">
          <Lock className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300">
            Course Coming Soon
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-md mx-auto">
            All lessons in this course are still being prepared. Check back soon
            for updates.
          </p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => navigate(basePath)}
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back to Course Library
          </Button>
        </div>
      </div>
    );
  }

  const isCurrentCompleted = selectedLesson
    ? !!completedLessons[selectedLesson.id]
    : false;

  return (
    <div className="flex flex-col md:flex-row h-[calc(100vh-8rem)] min-h-[600px] max-w-[1600px] mx-auto pb-20 md:pb-0">
      {/* Main Content */}
      <div className="flex-1 flex flex-col gap-4 min-w-0 overflow-auto px-4 md:px-6 py-4">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 truncate">
          {selectedLesson?.title || course.title}
        </h2>

        <PlayerVideoSection
          lesson={selectedLesson}
          hasPublishedLessons={publishedLessons.length > 0}
          onVideoEnded={handleVideoEnded}
        />

        {selectedLesson && (
          <>
            <PlayerControls
              isCompleted={isCurrentCompleted}
              previousLesson={previousLesson}
              nextLesson={nextLesson}
              onToggleComplete={handleToggleComplete}
              onPrevious={() => previousLesson && navigateToLesson(previousLesson)}
              onNext={() => nextLesson && navigateToLesson(nextLesson)}
              onCompleteAndNext={handleCompleteAndNext}
            />

            <PlayerLessonContent lesson={selectedLesson} />
          </>
        )}
      </div>

      {/* Desktop Sidebar */}
      <PlayerSidebarTOC
        course={course}
        selectedLessonId={selectedLesson?.id || null}
        expandedModules={expandedModules}
        completedLessons={completedLessons}
        progress={progress.percent}
        onToggleModule={toggleModule}
        onSelectLesson={navigateToLesson}
        onToggleLessonComplete={handleToggleLessonComplete}
      />

      {/* Mobile Bottom Nav */}
      <PlayerMobileNav
        hasPrevious={!!previousLesson}
        hasNext={!!nextLesson}
        onPrevious={() => previousLesson && navigateToLesson(previousLesson)}
        onNext={() => nextLesson && navigateToLesson(nextLesson)}
        onOpenContents={() => setShowMobileTOC(true)}
      />

      {/* Mobile TOC Sheet */}
      <PlayerMobileTOC
        open={showMobileTOC}
        onOpenChange={setShowMobileTOC}
        course={course}
        selectedLessonId={selectedLesson?.id || null}
        expandedModules={expandedModules}
        completedLessons={completedLessons}
        onToggleModule={toggleModule}
        onSelectLesson={navigateToLesson}
        onToggleLessonComplete={handleToggleLessonComplete}
      />
    </div>
  );
}
